import * as THREE from "three";

export class CollisionDetector {
  constructor() {
    // Reduced buffer for more forgiving collisions
    this.collisionBuffer = 0.0; // Removed buffer completely
  }

  // Simple AABB collision check between two bounds objects
  checkCollision(boundsA, boundsB) {
    if (
      boundsA.left < boundsB.right &&
      boundsA.right > boundsB.left &&
      boundsA.top > boundsB.bottom &&
      boundsA.bottom < boundsB.top
    ) {
      return true; // Collision detected
    }
    return false; // No collision
  }

  // Mesh-based collision detection (legacy method)
  checkCollisionWithMeshes(player, obstacles) {
    // Get player bounds
    const playerBounds = player.getBounds();

    // Add minimal buffer to player bounds (only for bottom and sides if needed)
    const bufferedPlayerBounds = {
      left: playerBounds.left - this.collisionBuffer,
      right: playerBounds.right + this.collisionBuffer,
      top: playerBounds.top, // No buffer at the top for more forgiving spike collisions
      bottom: playerBounds.bottom,
    };

    // Check collision with each obstacle
    for (const obstacle of obstacles) {
      let obstacleBounds;

      // Use userData bounds if available
      if (obstacle.userData && obstacle.userData.bounds) {
        obstacleBounds = obstacle.userData.bounds;

        // Special handling for triangle spikes (implemented in ObstacleManager)
        // Check if this is a spike obstacle (triangular)
        if (obstacle.userData.isSpike) {
          // For spikes, use a more forgiving triangle collision
          if (
            this.checkTriangleCollision(bufferedPlayerBounds, obstacleBounds)
          ) {
            return true; // Collision detected with triangle
          }
          continue; // Skip the regular AABB check for spikes
        }
      } else {
        // Fallback calculation if userData bounds aren't available
        obstacleBounds = {
          left: obstacle.position.x - obstacle.scale.x / 2,
          right: obstacle.position.x + obstacle.scale.x / 2,
          top: obstacle.position.y + obstacle.scale.y / 2,
          bottom: obstacle.position.y - obstacle.scale.y / 2,
        };
      }

      // Basic AABB collision check for non-spike obstacles
      if (this.checkCollision(bufferedPlayerBounds, obstacleBounds)) {
        return true; // Collision detected
      }
    }

    return false; // No collision
  }

  // Triangle-specific collision detection for spikes
  checkTriangleCollision(playerBounds, spikeBounds) {
    // First do a quick AABB check to see if we're even close to the spike
    if (
      playerBounds.left >= spikeBounds.right ||
      playerBounds.right <= spikeBounds.left ||
      playerBounds.top <= spikeBounds.bottom ||
      playerBounds.bottom >= spikeBounds.top
    ) {
      return false; // No collision possible
    }

    // If we're in the AABB area, do a more precise check

    // Calculate triangle area for collision
    const triangleBase = spikeBounds.right - spikeBounds.left;
    const triangleHeight = spikeBounds.top - spikeBounds.bottom;
    const spikeBaseY = spikeBounds.bottom;

    // Define a nearly pixel-perfect triangle with minimal forgiveness
    const forgivenessScale = 0.9; // Scale to 80% of original size

    // Calculate inset values to create a slightly smaller triangle
    const insetHorizontal = (triangleBase * (1 - forgivenessScale)) / 2;

    // Create triangle points
    const triangleLeft = spikeBounds.left + insetHorizontal;
    const triangleRight = spikeBounds.right - insetHorizontal;

    // Check if player is within the horizontal bounds of the triangle
    if (
      playerBounds.right <= triangleLeft ||
      playerBounds.left >= triangleRight
    ) {
      return false;
    }

    // Calculate what percentage of the way across the triangle base the player is
    // Use the player's bounds that are MOST inside the triangle for more accurate collision
    let playerX;

    // If player is coming from left side, use right edge
    if (playerBounds.right < (triangleLeft + triangleRight) / 2) {
      playerX = playerBounds.right;
    }
    // If player is coming from right side, use left edge
    else {
      playerX = playerBounds.left;
    }

    const relativePosX =
      (playerX - triangleLeft) / (triangleRight - triangleLeft);

    // Calculate the height of the triangle at that point using linear interpolation
    // For a triangle, the height increases linearly from the edges to the center
    // Left edge: 0% of height, Center: 100% of height, Right edge: 0% of height
    // This forms an isosceles triangle
    const heightAtX =
      triangleHeight *
      forgivenessScale *
      (1 - 2 * Math.abs(relativePosX - 0.5));

    // Calculate the y-position where the triangle edge is at the player's x-position
    const collisionY = spikeBaseY + heightAtX;

    // Check if player's bottom is below this collision point
    return playerBounds.bottom < collisionY;
  }

  // Check if player has fallen to death
  checkFallDeath(player) {
    return player.position.y < player.fallDeathLevel;
  }

  // Check for collision with projectiles
  checkProjectileCollision(playerBounds, projectiles) {
    // Add minimal buffer to player bounds (only for bottom and sides if needed)
    const bufferedPlayerBounds = {
      left: playerBounds.left - this.collisionBuffer,
      right: playerBounds.right + this.collisionBuffer,
      top: playerBounds.top, // No buffer at the top
      bottom: playerBounds.bottom,
    };

    for (const projectile of projectiles) {
      // Get projectile bounds from userData
      if (projectile.userData && projectile.userData.bounds) {
        const projectileBounds = projectile.userData.bounds;

        // Use basic AABB collision check
        if (this.checkCollision(bufferedPlayerBounds, projectileBounds)) {
          return true; // Collision detected
        }
      }
    }

    return false; // No collision
  }
}
